import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    motion,
    useMotionValue,
    useSpring,
    useTransform,
    useInView,
    useScroll,
    useReducedMotion,
    animate,
} from "framer-motion";
import {
    ArrowRight,
    Sparkles,
    CalendarCheck,
    FileText,
    LineChart,
    Repeat2,
    AudioLines,
    Check,
    Star,
    GraduationCap,
    Briefcase,
    PenTool,
} from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLine, ParallaxImage } from "@/components/Reveal";
import { PRODUCTS } from "@/data/products";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

/* HERO MEDIA — drop the final brand film URL here if it changes */
const HERO_VIDEO = "/assets/hero-video.mp4";
const HERO_POSTER = "/assets/hero-poster.jpeg";
const CLASSROOM_IMAGE = "/assets/classroom.jpeg";
const TEAM_IMAGE = "/assets/team-office.jpeg";
const NEURAL_IMAGE = "/assets/ai-neural.jpeg";

const STATS = [
    { to: 2400, suffix: "+", label: "coaches & teams craft with us" },
    { to: 38000, suffix: "+", label: "sessions planned with AI" },
    { to: 12, suffix: " hrs", label: "saved per coach, every week" },
    { static: "4.9 / 5", label: "average customer rating" },
];

const WORDMARKS = ["Northbeam Coaching", "Vantage Group", "Lumen Labs", "Atlas & Co", "Heartwork Studio"];

const STEPS = [
    {
        n: "01",
        title: "Describe your coaching style",
        text: "Answer a short guided brief — your niche, your method, your voice. The platform builds your coaching profile from it.",
    },
    {
        n: "02",
        title: "Generate programs & session plans",
        text: "AI drafts complete coaching programs and session-by-session plans, structured the way you already work.",
    },
    {
        n: "03",
        title: "Personalise worksheets per client",
        text: "Every client gets worksheets and exercises tuned to their goals, generated in seconds and editable by you.",
    },
    {
        n: "04",
        title: "Track progress & refine",
        text: "Progress insights show what is landing and what is not, so every program gets sharper with each cohort.",
    },
];

const FEATURES = [
    {
        icon: Sparkles,
        title: "AI Program Builder",
        text: "Turn a coaching brief into a structured multi-week program — outcomes, milestones and session arcs included.",
    },
    {
        icon: CalendarCheck,
        title: "Session Planner",
        text: "Generate agenda, talking points and exercises for every session, matched to where each client is in their journey.",
    },
    {
        icon: FileText,
        title: "Worksheet Generator",
        text: "Beautiful, branded client worksheets drafted in your voice — reflection prompts, action plans and trackers.",
    },
    {
        icon: LineChart,
        title: "Client Progress Insights",
        text: "See momentum, blockers and engagement across your whole practice, with gentle nudges when a client goes quiet.",
    },
    {
        icon: Repeat2,
        title: "Content Repurposing",
        text: "One coaching framework becomes posts, emails and workshop outlines — your ideas, multiplied across channels.",
    },
    {
        icon: AudioLines,
        title: "Brand Voice",
        text: "Train the AI on your writing once, and everything it drafts — programs to posts — sounds unmistakably like you.",
    },
];

const AUDIENCES = [
    {
        icon: GraduationCap,
        title: "Coaches",
        text: "Life, leadership, fitness or career — spend your hours coaching while the platform handles the paperwork of transformation.",
    },
    {
        icon: Briefcase,
        title: "Consultants",
        text: "Package your method into repeatable programs, proposals and client materials without rebuilding from scratch each time.",
    },
    {
        icon: PenTool,
        title: "Creators",
        text: "Turn what you know into courses, cohorts and communities — with worksheets and content that stay on-brand.",
    },
];

const TIERS = [
    {
        name: "Starter",
        monthly: 999,
        annual: 799,
        blurb: "For coaches launching their first structured program.",
        features: ["1 coach seat", "10 active clients", "AI Program Builder", "Session Planner", "Community support"],
        cta: "Start free trial",
        featured: false,
    },
    {
        name: "Studio",
        monthly: 2499,
        annual: 1999,
        blurb: "For growing practices that live inside the platform.",
        features: [
            "5 coach seats",
            "Unlimited clients",
            "Worksheet Generator",
            "Client Progress Insights",
            "Brand Voice training",
            "Priority support",
        ],
        cta: "Start free trial",
        featured: true,
    },
    {
        name: "Scale",
        monthly: 4999,
        annual: 3999,
        blurb: "For studios, teams and creator businesses.",
        features: [
            "Unlimited seats",
            "Everything in Studio",
            "Content Repurposing",
            "API & integrations",
            "SSO & advanced security",
            "Dedicated success manager",
        ],
        cta: "Talk to sales",
        featured: false,
    },
];

const TESTIMONIALS = [
    {
        quote: "I used to spend Sundays building session plans. Now I spend them with my family — and honestly, the plans are better than the ones I sweated over.",
        name: "Anaya Deshmukh",
        role: "Leadership Coach, Mumbai",
        avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=250",
    },
    {
        quote: "The worksheet generator alone pays for the subscription. My clients are convinced I quietly hired a content team.",
        name: "Marcus Feld",
        role: "Business Consultant, Berlin",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=250",
    },
    {
        quote: "It drafts in my voice so well that I have stopped editing. For someone who rewrites everything twice, that is the highest praise I have.",
        name: "Priya Raghavan",
        role: "Creator & Mentor, Bengaluru",
        avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=250",
    },
];

const FAQS = [
    {
        q: "Do I need technical skills to use the platform?",
        a: "None at all. If you can describe your coaching style in a paragraph, you can use Coaching Crafters AI. The guided brief walks you through setup, and every draft is editable in a familiar, clean editor.",
    },
    {
        q: "How does the AI learn my voice?",
        a: "You share a few samples of your writing — emails, posts, session notes — and the Brand Voice engine builds a style profile. Everything it drafts afterwards, from worksheets to social posts, follows that profile.",
    },
    {
        q: "Can I edit what the AI drafts?",
        a: "Everything is a draft until you say otherwise. Programs, session plans and worksheets are fully editable, and the AI learns from your edits to get closer to your style over time.",
    },
    {
        q: "Is my client data private?",
        a: "Yes. Your data is encrypted in transit and at rest, and it is never used to train models for other customers. You can export or delete your data at any time.",
    },
    {
        q: "Can I cancel my subscription anytime?",
        a: "Monthly plans can be cancelled anytime and stay active until the end of the billing period. Please note that, as stated in our Refund Policy, subscription and licence fees are non-refundable once a billing period begins.",
    },
    {
        q: "Does it connect with the tools I already use?",
        a: "Yes — calendar, documents and payment tools integrate out of the box, and the Scale plan adds API access for anything custom.",
    },
];

const CountUp = ({ to, suffix = "" }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const reduce = useReducedMotion();
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return undefined;
        if (reduce) {
            setVal(to);
            return undefined;
        }
        const controls = animate(0, to, {
            duration: 1.8,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setVal(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, to, reduce]);

    return (
        <span ref={ref} data-testid={`stat-counter-${to}`}>
            {val.toLocaleString("en-IN")}
            {suffix}
        </span>
    );
};

const ScrollProgress = () => {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.4 });
    return (
        <motion.div
            data-testid="scroll-progress-bar"
            style={{ scaleX }}
            className="fixed left-0 top-0 z-[60] h-0.5 w-full origin-left bg-gradient-to-r from-ember via-ember-soft to-ember"
        />
    );
};

const HeroVisual = () => {
    const ref = useRef(null);
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const rotateX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 16 });
    const rotateY = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 16 });

    const handleMove = (e) => {
        const rect = ref.current.getBoundingClientRect();
        mx.set((e.clientX - rect.left) / rect.width);
        my.set((e.clientY - rect.top) / rect.height);
    };
    const handleLeave = () => {
        mx.set(0.5);
        my.set(0.5);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
            style={{ perspective: 1200 }}
        >
            <div
                ref={ref}
                onMouseMove={handleMove}
                onMouseLeave={handleLeave}
                className="relative"
                data-testid="hero-visual"
            >
                <div className="absolute -inset-8 rounded-[2.5rem] bg-ember/25 blur-3xl" aria-hidden="true" />
                <motion.div
                    style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                    className="relative overflow-hidden rounded-3xl border border-white/20 bg-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl"
                >
                    <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3">
                        <span className="h-2.5 w-2.5 rounded-full bg-ember/80" />
                        <span className="h-2.5 w-2.5 rounded-full bg-ember-soft/80" />
                        <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                        <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                            studio.coachingcraftersai.com
                        </span>
                    </div>
                    <img
                        src={CLASSROOM_IMAGE}
                        alt="Smart classroom with students on tablets and a teacher at an interactive panel"
                        className="aspect-[16/10] w-full object-cover"
                        loading="eager"
                    />
                    <motion.div
                        style={{ transform: "translateZ(50px)" }}
                        className="absolute -left-4 bottom-10 hidden rounded-2xl border border-white/15 bg-obsidian/85 px-4 py-3 shadow-xl backdrop-blur-md sm:block"
                    >
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ember-soft">AI Draft ready</p>
                        <p className="mt-1 text-sm font-semibold text-[#F5F5F4]">Week 4 session plan generated</p>
                    </motion.div>
                    <motion.div
                        style={{ transform: "translateZ(70px)" }}
                        className="absolute -right-3 top-14 hidden rounded-2xl border border-white/15 bg-obsidian/85 px-4 py-3 shadow-xl backdrop-blur-md sm:block"
                    >
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ember-soft">Insights</p>
                        <p className="mt-1 text-sm font-semibold text-[#F5F5F4]">
                            Client momentum <span className="text-ember-soft">+24%</span>
                        </p>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
};

const SectionHeading = ({ overline, title, sub, dark = false }) => (
    <Reveal className="mx-auto max-w-3xl text-center">
        <p className={`font-mono text-xs uppercase tracking-[0.25em] ${dark ? "text-ember-soft" : "text-ember"}`}>
            {overline}
        </p>
        <h2
            className={`mt-4 font-serif text-3xl font-semibold leading-[1.15] tracking-tight sm:text-4xl ${
                dark ? "text-[#F5F5F4]" : "text-ink"
            }`}
        >
            {title}
        </h2>
        {sub && (
            <p className={`mt-4 text-base leading-relaxed sm:text-lg ${dark ? "text-[#F5F5F4]/60" : "text-ink/60"}`}>
                {sub}
            </p>
        )}
    </Reveal>
);

const Home = () => {
    const [annual, setAnnual] = useState(true);
    const reduceMotion = useReducedMotion();
    const heroVideoRef = useRef(null);
    const ctaVideoRef = useRef(null);

    useEffect(() => {
        if (reduceMotion) return;
        [heroVideoRef.current, ctaVideoRef.current].forEach((v) => {
            if (v) v.play().catch(() => {});
        });
    }, [reduceMotion]);

    return (
        <>
            <Seo
                title="Coaching Crafters AI — AI-Powered Coaching Platform"
                description="Coaching Crafters AI helps coaches, consultants and creators craft coaching programs, session plans, client worksheets and content with AI — in minutes, not weeks."
                path="/"
            />
            <ScrollProgress />

            {/* HERO — video background */}
            <section className="relative overflow-hidden bg-obsidian" data-testid="hero-section">
                {reduceMotion ? (
                    <img
                        src={HERO_POSTER}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                ) : (
                    <video
                        ref={heroVideoRef}
                        data-testid="hero-video"
                        className="absolute inset-0 h-full w-full object-cover"
                        src={HERO_VIDEO}
                        poster={HERO_POSTER}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        aria-hidden="true"
                    />
                )}
                <div
                    className="absolute inset-0 bg-gradient-to-r from-obsidian/90 via-obsidian/60 to-obsidian/30"
                    aria-hidden="true"
                />
                <div
                    className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-obsidian/90 to-transparent"
                    aria-hidden="true"
                />
                {!reduceMotion && (
                    <>
                        <motion.div
                            aria-hidden="true"
                            animate={{ y: [0, -24, 0], x: [0, 12, 0] }}
                            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-ember/20 blur-3xl"
                        />
                        <motion.div
                            aria-hidden="true"
                            animate={{ y: [0, 20, 0], x: [0, -14, 0] }}
                            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -right-20 bottom-1/4 h-80 w-80 rounded-full bg-ember-soft/15 blur-3xl"
                        />
                    </>
                )}

                <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 pb-24 pt-16 sm:px-10 lg:grid-cols-2 lg:pb-32 lg:pt-24">
                    <div>
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-ember-soft"
                            data-testid="hero-overline"
                        >
                            Coaching Crafters AI · AI-Powered Solutions
                        </motion.p>
                        <h1 className="mt-6 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-[#F5F5F4] sm:text-5xl lg:text-6xl">
                            <MaskedLine delay={0.15}>Craft coaching that</MaskedLine>
                            <MaskedLine delay={0.32}>
                                <em className="font-medium italic text-ember-soft">changes people.</em>
                            </MaskedLine>
                        </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.55 }}
                            className="mt-6 max-w-xl text-base leading-relaxed text-[#F5F5F4]/70 sm:text-lg"
                            data-testid="hero-subheadline"
                        >
                            Coaching Crafters AI drafts your programs, session plans, worksheets and content in your
                            voice — so you spend your hours coaching, not formatting documents. Behind it, our studio
                            builds the software, ERP, panels and solar systems that help whole organisations achieve
                            all goals.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.7 }}
                            className="mt-9 flex flex-wrap items-center gap-4"
                        >
                            <Link
                                to="/login?mode=signup"
                                data-testid="hero-trial-button"
                                className="group inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 text-sm font-semibold text-bone shadow-[0_10px_30px_rgba(30,79,163,0.45)] transition-all duration-200 hover:bg-ember-dark hover:shadow-[0_14px_36px_rgba(30,79,163,0.55)]"
                            >
                                Start free trial
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/contact?demo=1"
                                data-testid="hero-demo-button"
                                className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-[#F5F5F4] transition-colors duration-200 hover:border-ember hover:text-ember-soft"
                            >
                                Book a demo
                            </Link>
                        </motion.div>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.7, delay: 0.9 }}
                            className="mt-5 text-xs text-[#F5F5F4]/40"
                        >
                            14-day free trial · No credit card needed · Cancel anytime
                        </motion.p>
                    </div>
                    <HeroVisual />
                </div>
                <motion.div
                    aria-hidden="true"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.4, delay: 1, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute bottom-0 left-0 h-[3px] w-full origin-left bg-gradient-to-r from-ember via-ember-soft to-transparent"
                />
            </section>

            {/* SOCIAL PROOF */}
            <section className="border-b border-[#E2E8F4] bg-white/60" data-testid="social-proof-section">
                <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10">
                    <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
                        {STATS.map((s, i) => (
                            <Reveal key={s.label} delay={i * 0.08} className="text-center lg:text-left">
                                <p className="font-serif text-3xl font-semibold text-ink sm:text-4xl">
                                    {s.static ? s.static : <CountUp to={s.to} suffix={s.suffix} />}
                                </p>
                                <p className="mt-1 text-xs text-ink/50 sm:text-sm">{s.label}</p>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 border-t border-[#E2E8F4] pt-8">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">
                            Trusted by teams at
                        </span>
                        {WORDMARKS.map((w) => (
                            <span key={w} className="font-serif text-lg italic text-ink/35">
                                {w}
                            </span>
                        ))}
                    </Reveal>
                    <Reveal className="mt-6 text-center">
                        <span
                            data-testid="trust-entity-line"
                            className="inline-block rounded-full border border-[#E2E8F4] bg-bone px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50"
                        >
                            A Sarvacharya Education Pvt Ltd company
                        </span>
                    </Reveal>
                </div>
            </section>

            <Marquee />

            {/* WHO WE ARE */}
            <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28" data-testid="who-we-are-section">
                <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
                    <Reveal className="relative">
                        <div className="absolute -inset-6 rounded-[2.5rem] bg-ember/10 blur-3xl" aria-hidden="true" />
                        <ParallaxImage
                            src={TEAM_IMAGE}
                            alt="The Coaching Crafters AI team collaborating in a modern office"
                            className="relative aspect-[4/3] rounded-3xl border border-white/40 shadow-[0_40px_80px_-24px_rgba(26,25,24,0.35)]"
                        />
                    </Reveal>
                    <div>
                        <Reveal>
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember">Who we are</p>
                            <h2 className="mt-4 font-serif text-3xl font-semibold leading-[1.15] tracking-tight text-ink sm:text-4xl">
                                A brand built on craft, backed by <em className="italic text-ember">Sarvacharya.</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-base leading-relaxed text-ink/65">
                                Coaching Crafters AI is a brand of{" "}
                                <span className="font-semibold text-ink">Sarvacharya Education Pvt Ltd</span> — an
                                education-focused technology company. From a single classroom to a multi-location
                                enterprise, we pair deep engineering with hands-on support, so technology never becomes
                                another thing to manage.
                            </p>
                            <p className="mt-4 text-base leading-relaxed text-ink/65">
                                We call it crafting, because every deployment is shaped around the way you actually
                                work — and we sign off on every project the same way our clients close their goals:{" "}
                                <span className="font-serif italic text-ember">achieving all goals.</span>
                            </p>
                            <Link
                                to="/about"
                                data-testid="who-we-are-about-link"
                                className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-ember"
                            >
                                Read our story
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* PRODUCTS PREVIEW */}
            <section className="mx-auto max-w-7xl px-6 pb-20 sm:px-10 lg:pb-28" data-testid="products-preview-section">
                <SectionHeading
                    overline="From the Crafters studio"
                    title="One studio. Four ways to craft."
                    sub="Beyond the coaching platform, we design and deliver the technology organisations run on — software, systems, panels and power."
                />
                <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {PRODUCTS.map((p, i) => (
                        <Reveal key={p.slug} delay={i * 0.08}>
                            <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.25 }}>
                                <Link
                                    to={`/products/${p.slug}`}
                                    data-testid={`home-product-${p.slug}-card`}
                                    className="group block overflow-hidden rounded-3xl border border-[#E2E8F4] bg-white shadow-sm transition-shadow duration-300 hover:shadow-[0_24px_50px_-20px_rgba(30,79,163,0.3)]"
                                >
                                    <div className="overflow-hidden">
                                        <img
                                            src={p.image}
                                            alt={p.imageAlt}
                                            className="aspect-[3/2] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="font-serif text-xl font-semibold text-ink">{p.name}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-ink/55">{p.tagline}</p>
                                        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-ember">
                                            Explore
                                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                                        </span>
                                    </div>
                                </Link>
                            </motion.div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* IN THE CLASSROOM / IMPACT */}
            <section className="border-y border-[#E2E8F4] bg-sand/60" data-testid="impact-section">
                <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-20 sm:px-10 lg:grid-cols-2 lg:py-28">
                    <div>
                        <Reveal>
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember">Our impact</p>
                            <h2 className="mt-4 font-serif text-3xl font-semibold leading-[1.15] tracking-tight text-ink sm:text-4xl">
                                In the classroom, on the roof, in the boardroom
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-base leading-relaxed text-ink/65">
                                Our work shows up where it matters: students leaning into a lesson on an interactive
                                panel, a finance head closing the month without spreadsheet gymnastics, a rooftop
                                quietly paying for itself in units of power.
                            </p>
                            <ul className="mt-7 space-y-4">
                                {[
                                    "Interactive panels live in classrooms and boardrooms across the regions we serve",
                                    "AI and ERP deployments that remove hours of manual work every week",
                                    "Solar systems engineered end to end — survey to subsidy to switch-on",
                                ].map((line) => (
                                    <li key={line} className="flex items-start gap-3 text-sm leading-relaxed text-ink/70">
                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-ember" />
                                        {line}
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/products"
                                data-testid="impact-products-link"
                                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-semibold text-bone transition-colors hover:bg-ember-dark"
                            >
                                Explore the products
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                    <Reveal className="relative">
                        <div className="absolute -inset-6 rounded-[2.5rem] bg-ember/10 blur-3xl" aria-hidden="true" />
                        <ParallaxImage
                            src={CLASSROOM_IMAGE}
                            alt="Students on tablets while a teacher presents on an interactive panel"
                            className="relative aspect-[4/3] rounded-3xl border border-white/40 shadow-[0_40px_80px_-24px_rgba(26,25,24,0.35)]"
                        />
                    </Reveal>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how-it-works" className="grain relative bg-obsidian" data-testid="how-it-works-section">
                <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28">
                    <SectionHeading
                        dark
                        overline="How it works"
                        title="From blank page to full program in four steps"
                        sub="No prompt engineering, no templates graveyard — just your method, structured by AI."
                    />
                    <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.1}>
                                <div
                                    data-testid={`how-it-works-step-${i + 1}`}
                                    className="group relative h-full rounded-3xl border border-white/10 bg-obsidian-card p-7 transition-colors duration-300 hover:border-ember/50"
                                >
                                    <span className="font-serif text-5xl font-semibold text-ember/30 transition-colors duration-300 group-hover:text-ember">
                                        {s.n}
                                    </span>
                                    <h3 className="mt-5 text-lg font-semibold text-[#F5F5F4]">{s.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-[#F5F5F4]/55">{s.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="features" className="relative overflow-hidden" data-testid="features-section">
                <img
                    src={NEURAL_IMAGE}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.05]"
                />
                <div className="relative mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28">
                    <SectionHeading
                        overline="Features"
                        title="Everything between the idea and the invoice"
                        sub="Six tools that carry a coaching practice from first draft to thriving client roster."
                    />
                    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {FEATURES.map((f, i) => (
                            <Reveal key={f.title} delay={(i % 3) * 0.08}>
                                <motion.div
                                    whileHover={{ y: -6 }}
                                    transition={{ duration: 0.25 }}
                                    data-testid={`feature-card-${i + 1}`}
                                    className="h-full rounded-3xl border border-[#E2E8F4] bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-[0_24px_50px_-20px_rgba(30,79,163,0.25)]"
                                >
                                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ember/10">
                                        <f.icon className="h-6 w-6 text-ember" />
                                    </span>
                                    <h3 className="mt-5 text-lg font-semibold text-ink">{f.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-ink/55">{f.text}</p>
                                </motion.div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHO IT'S FOR */}
            <section className="border-y border-[#E2E8F4] bg-sand/60" data-testid="audiences-section">
                <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28">
                    <SectionHeading overline="Who it's for" title="Built for people who build people" />
                    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {AUDIENCES.map((a, i) => (
                            <Reveal key={a.title} delay={i * 0.1}>
                                <motion.div
                                    whileHover={{ y: -6 }}
                                    transition={{ duration: 0.25 }}
                                    data-testid={`audience-card-${a.title.toLowerCase()}`}
                                    className="h-full rounded-3xl border border-[#E2E8F4] bg-bone p-8"
                                >
                                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-ember text-bone">
                                        <a.icon className="h-5 w-5" />
                                    </span>
                                    <h3 className="mt-5 font-serif text-2xl font-semibold text-ink">{a.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-ink/60">{a.text}</p>
                                </motion.div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28" data-testid="pricing-section">
                <SectionHeading
                    overline="Pricing"
                    title="Simple plans, priced in rupees"
                    sub="Start free for 14 days. Upgrade when your practice does."
                />
                <Reveal className="mt-10 flex items-center justify-center gap-4">
                    <span className={`text-sm font-medium ${!annual ? "text-ink" : "text-ink/40"}`}>Monthly</span>
                    <button
                        data-testid="pricing-billing-toggle"
                        onClick={() => setAnnual((v) => !v)}
                        aria-label="Toggle annual billing"
                        className={`relative h-7 rounded-full px-0.5 transition-colors duration-300 ${annual ? "bg-ember" : "bg-ink/20"}`}
                        style={{ width: "3.25rem" }}
                    >
                        <motion.span
                            layout
                            className="block h-6 w-6 rounded-full bg-white shadow"
                            style={{ marginLeft: annual ? "auto" : 0 }}
                        />
                    </button>
                    <span className={`text-sm font-medium ${annual ? "text-ink" : "text-ink/40"}`}>
                        Annual{" "}
                        <span className="ml-1 rounded-full bg-ember/10 px-2 py-0.5 text-xs font-semibold text-ember">
                            save 20%
                        </span>
                    </span>
                </Reveal>
                <div className="mt-12 grid grid-cols-1 items-stretch gap-8 lg:grid-cols-3">
                    {TIERS.map((t, i) => (
                        <Reveal key={t.name} delay={i * 0.1} className="h-full">
                            <motion.div
                                whileHover={{ y: -6 }}
                                transition={{ duration: 0.25 }}
                                data-testid={`pricing-card-${t.name.toLowerCase()}`}
                                className={`relative flex h-full flex-col rounded-3xl p-8 ${
                                    t.featured
                                        ? "grain bg-obsidian text-[#F5F5F4] shadow-[0_30px_60px_-20px_rgba(15,17,21,0.5)] ring-2 ring-ember"
                                        : "border border-[#E2E8F4] bg-white text-ink"
                                }`}
                            >
                                {t.featured && (
                                    <span
                                        data-testid="pricing-recommended-badge"
                                        className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-ember px-4 py-1 text-xs font-semibold uppercase tracking-wider text-bone"
                                    >
                                        Recommended
                                    </span>
                                )}
                                <h3 className="font-serif text-2xl font-semibold">{t.name}</h3>
                                <p className={`mt-2 text-sm ${t.featured ? "text-[#F5F5F4]/60" : "text-ink/55"}`}>{t.blurb}</p>
                                <p className="mt-6">
                                    <span className="font-serif text-5xl font-bold">
                                        ₹{(annual ? t.annual : t.monthly).toLocaleString("en-IN")}
                                    </span>
                                    <span className={`ml-2 text-sm ${t.featured ? "text-[#F5F5F4]/50" : "text-ink/45"}`}>
                                        / month{annual ? ", billed annually" : ""}
                                    </span>
                                </p>
                                <ul className="mt-7 flex-1 space-y-3">
                                    {t.features.map((f) => (
                                        <li key={f} className="flex items-start gap-3 text-sm">
                                            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${t.featured ? "text-ember-soft" : "text-ember"}`} />
                                            <span className={t.featured ? "text-[#F5F5F4]/80" : "text-ink/70"}>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to={t.name === "Scale" ? "/contact" : "/login?mode=signup"}
                                    data-testid={`pricing-${t.name.toLowerCase()}-cta`}
                                    className={`mt-8 rounded-full py-3.5 text-center text-sm font-semibold transition-colors duration-200 ${
                                        t.featured
                                            ? "bg-ember text-bone hover:bg-ember-dark"
                                            : "border border-ink/15 text-ink hover:border-ember hover:text-ember"
                                    }`}
                                >
                                    {t.cta}
                                </Link>
                            </motion.div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="grain relative bg-obsidian" data-testid="testimonials-section">
                <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28">
                    <SectionHeading dark overline="Testimonials" title="Coaches talk. We listen." />
                    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {TESTIMONIALS.map((t, i) => (
                            <Reveal key={t.name} delay={i * 0.1}>
                                <motion.figure
                                    whileHover={{ y: -6 }}
                                    transition={{ duration: 0.25 }}
                                    data-testid={`testimonial-card-${i + 1}`}
                                    className="flex h-full flex-col rounded-3xl border border-white/10 bg-obsidian-card p-8"
                                >
                                    <div className="flex gap-1">
                                        {[...Array(5)].map((_, s) => (
                                            <Star key={s} className="h-4 w-4 fill-ember text-ember" />
                                        ))}
                                    </div>
                                    <blockquote className="mt-5 flex-1 font-serif text-lg italic leading-relaxed text-[#F5F5F4]/85">
                                        “{t.quote}”
                                    </blockquote>
                                    <figcaption className="mt-6 flex items-center gap-3">
                                        <img
                                            src={t.avatar}
                                            alt={`Portrait of ${t.name}`}
                                            className="h-11 w-11 rounded-full object-cover ring-2 ring-ember/40"
                                            loading="lazy"
                                        />
                                        <div>
                                            <p className="text-sm font-semibold text-[#F5F5F4]">{t.name}</p>
                                            <p className="text-xs text-[#F5F5F4]/50">{t.role}</p>
                                        </div>
                                    </figcaption>
                                </motion.figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="mx-auto max-w-4xl px-6 py-20 sm:px-10 lg:py-28" data-testid="faq-section">
                <SectionHeading overline="FAQ" title="Questions, answered plainly" />
                <Reveal className="mt-12">
                    <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                        {FAQS.map((f, i) => (
                            <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-[#E2E8F4]">
                                <AccordionTrigger
                                    data-testid={`faq-question-${i + 1}`}
                                    className="py-5 text-left text-base font-semibold text-ink hover:text-ember hover:no-underline"
                                >
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="pb-5 text-sm leading-relaxed text-ink/60">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Reveal>
            </section>

            {/* FINAL CTA — video accent */}
            <section className="relative overflow-hidden bg-obsidian" data-testid="final-cta-section">
                {!reduceMotion && (
                    <video
                        ref={ctaVideoRef}
                        data-testid="cta-video"
                        className="absolute inset-0 h-full w-full object-cover opacity-30"
                        src={HERO_VIDEO}
                        poster={HERO_POSTER}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        aria-hidden="true"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/70 to-obsidian" aria-hidden="true" />
                <div className="relative mx-auto max-w-4xl px-6 py-24 text-center sm:px-10 lg:py-32">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember-soft">
                            Coaching Crafters AI
                        </p>
                        <h2 className="mt-5 font-serif text-3xl font-semibold leading-tight text-[#F5F5F4] sm:text-5xl">
                            Your next cohort deserves your <em className="italic text-ember-soft">best craft.</em>
                        </h2>
                        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#F5F5F4]/60">
                            Start your free trial today, or book a demo and let us show you what your coaching looks
                            like with the busywork removed.
                        </p>
                        <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                            <Link
                                to="/login?mode=signup"
                                data-testid="final-cta-trial-button"
                                className="group inline-flex items-center gap-2 rounded-full bg-ember px-8 py-4 text-sm font-semibold text-bone shadow-[0_10px_30px_rgba(30,79,163,0.45)] transition-colors hover:bg-ember-dark"
                            >
                                Start free trial
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/contact?demo=1"
                                data-testid="final-cta-demo-button"
                                className="rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-[#F5F5F4] transition-colors hover:border-ember hover:text-ember-soft"
                            >
                                Book a demo
                            </Link>
                        </div>
                        <p className="mt-8 text-xs text-[#F5F5F4]/35">
                            Coaching Crafters AI is a brand of Sarvacharya Education Pvt Ltd.
                        </p>
                    </Reveal>
                </div>
            </section>
        </>
    );
};

export default Home;
